import { useState } from 'react';
import type { GameId } from '@ofc/shared';
import { Fold } from '@ofc/ui';
import type { GameCard } from './Landing';

/**
 * The games on the front page, in sections by kind, with chips along the top to
 * show one kind or only the games that suit how many of you there are.
 */

export type GameCategory = 'party' | 'film' | 'gaming' | 'board' | 'cards' | 'word';

export const GAME_CATEGORIES: { id: GameCategory; label: string; chip: string; icon: string }[] = [
  { id: 'party', label: 'Party games', chip: 'Party', icon: '🎉' },
  { id: 'film', label: 'Film challenges', chip: 'Film', icon: '🎬' },
  { id: 'gaming', label: 'Video games', chip: 'Gaming', icon: '🎮' },
  { id: 'board', label: 'Board games', chip: 'Board', icon: '♟️' },
  { id: 'cards', label: 'Cards and dice', chip: 'Cards', icon: '🃏' },
  { id: 'word', label: 'Word games', chip: 'Word', icon: '🔤' },
];

/** Each game's kind, and the fewest and most players it takes. */
const META: Record<GameId, { category: GameCategory; min: number; max: number }> = {
  movie: { category: 'film', min: 1, max: 12 },
  quiz: { category: 'party', min: 1, max: 10 },
  quiplash: { category: 'party', min: 3, max: 10 },
  fibbage: { category: 'party', min: 3, max: 10 },
  enough: { category: 'party', min: 4, max: 10 },
  wavelengths: { category: 'party', min: 2, max: 10 },
  obscurity: { category: 'party', min: 1, max: 10 },
  superlatives: { category: 'party', min: 3, max: 10 },
  chameleon: { category: 'party', min: 3, max: 10 },
  hottakes: { category: 'party', min: 3, max: 10 },
  whosaid: { category: 'party', min: 3, max: 10 },
  wouldyourather: { category: 'party', min: 2, max: 10 },
  cheat: { category: 'cards', min: 3, max: 8 },
  rankem: { category: 'party', min: 3, max: 10 },
  spotlight: { category: 'party', min: 3, max: 10 },
  blindranking: { category: 'party', min: 1, max: 10 },
  onestar: { category: 'film', min: 3, max: 10 },
  wagerquiz: { category: 'party', min: 1, max: 10 },
  fakinit: { category: 'party', min: 4, max: 10 },
  undercover: { category: 'party', min: 4, max: 10 },
  mole: { category: 'party', min: 4, max: 10 },
  boxart: { category: 'gaming', min: 1, max: 8 },
  higherlower: { category: 'film', min: 1, max: 4 },
  castreveal: { category: 'film', min: 1, max: 4 },
  posterzoom: { category: 'film', min: 1, max: 4 },
  sixdegrees: { category: 'film', min: 1, max: 4 },
  connect4: { category: 'board', min: 2, max: 2 },
  noughts: { category: 'board', min: 2, max: 2 },
  battleships: { category: 'board', min: 2, max: 2 },
  dots: { category: 'board', min: 2, max: 2 },
  poker: { category: 'cards', min: 2, max: 8 },
  liarsdice: { category: 'cards', min: 2, max: 6 },
  wordduel: { category: 'word', min: 1, max: 4 },
  anagrams: { category: 'word', min: 1, max: 4 },
};

export function gameCategory(game: GameId): GameCategory {
  return META[game].category;
}

export type Players = 'any' | 'solo' | 'two' | 'group';

export const PLAYER_CHIPS: { id: Players; label: string }[] = [
  { id: 'solo', label: 'Solo' },
  { id: 'two', label: '2 players' },
  { id: 'group', label: '3+' },
];

export function fits(game: GameId, players: Players): boolean {
  const m = META[game];
  if (players === 'solo') return m.min === 1;
  if (players === 'two') return m.min <= 2 && m.max >= 2;
  if (players === 'group') return m.max >= 3;
  return true;
}

/** Whether exactly this many people can all play it together. */
export function fitsCount(game: GameId, count: number): boolean {
  const m = META[game];
  return count >= m.min && count <= m.max;
}

export function playersLabel(game: GameId): string {
  const { min, max } = META[game];
  if (min === max) return `${min} players`;
  return min === 1 ? `1–${max}` : `${min}–${max}`;
}

// The chips remember their choice on this phone; a private window just starts fresh.
export function remembered<T extends string>(key: string, fallback: T): T {
  try {
    return (localStorage.getItem(key) as T | null) ?? fallback;
  } catch {
    return fallback;
  }
}

export function remember(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Not stored; fine.
  }
}

export function GameList({ games, onHost }: { games: GameCard[]; onHost(game: GameId): void }) {
  const [category, setCategory] = useState<GameCategory | 'all'>(() => remembered('ofc:games:category', 'all'));
  const [players, setPlayers] = useState<Players>(() => remembered('ofc:games:players', 'any'));
  const pickCategory = (c: GameCategory | 'all') => {
    setCategory(c);
    remember('ofc:games:category', c);
  };
  const pickPlayers = (p: Players) => {
    const next = players === p ? 'any' : p;
    setPlayers(next);
    remember('ofc:games:players', next);
  };

  const shown = games.filter((g) => (category === 'all' || META[g.id].category === category) && fits(g.id, players));
  const sections = GAME_CATEGORIES.map((c) => ({ ...c, games: shown.filter((g) => META[g.id].category === c.id) })).filter((s) => s.games.length > 0);

  return (
    <div className="col" style={{ gap: 10 }}>
      <div className="chip-row" role="group" aria-label="Show games by kind">
        <button className={`chip${category === 'all' ? ' on' : ''}`} onClick={() => pickCategory('all')} aria-pressed={category === 'all'}>
          All
        </button>
        {GAME_CATEGORIES.map((c) => (
          <button key={c.id} className={`chip${category === c.id ? ' on' : ''}`} onClick={() => pickCategory(c.id)} aria-pressed={category === c.id}>
            {c.icon} {c.chip}
          </button>
        ))}
      </div>
      <div className="chip-row" role="group" aria-label="Show games by number of players">
        {PLAYER_CHIPS.map((p) => (
          <button key={p.id} className={`chip small${players === p.id ? ' on' : ''}`} onClick={() => pickPlayers(p.id)} aria-pressed={players === p.id}>
            👤 {p.label}
          </button>
        ))}
      </div>

      {sections.length === 0 && <p className="small muted center">No games match. Try another filter.</p>}
      {sections.map((s) => {
        const body = s.games.map((game) => <GameButton key={game.id} game={game} onHost={onHost} />);
        // With one kind picked, no need for its fold as well.
        return category === 'all' ? (
          <Fold key={s.id} id={`landing:cat:${s.id}`} label={`${s.icon} ${s.label}`} summary={`${s.games.length}`} defaultOpen>
            {body}
          </Fold>
        ) : (
          <div key={s.id} className="col" style={{ gap: 8 }}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

function GameButton({ game, onHost }: { game: GameCard; onHost(game: GameId): void }) {
  return (
    <button
      className="panel row"
      disabled={!game.available}
      onClick={() => onHost(game.id)}
      style={{
        gap: 12,
        textAlign: 'left',
        padding: 14,
        minHeight: 76,
        borderColor: game.available ? game.glow : 'var(--panel-line)',
        boxShadow: game.available ? `0 0 18px ${game.glow}33` : 'none',
      }}
    >
      {/* Fixed width: emoji render at different widths, and without it
          the Wavelengths card's text sat a few pixels left of the rest. */}
      <span aria-hidden style={{ fontSize: 28, width: 36, flexShrink: 0, textAlign: 'center' }}>
        {game.icon}
      </span>
      <span className="grow col" style={{ gap: 2, minWidth: 0 }}>
        <span className="sign" style={{ fontSize: 17 }}>
          {game.title}
        </span>
        <span className="small muted">{game.tagline}</span>
      </span>
      <span className="col" style={{ alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
        <span className="sign small" style={{ color: game.available ? game.glow : 'var(--muted)' }}>
          {game.available ? 'Host' : 'Soon'}
        </span>
        <span className="small muted" style={{ fontSize: 11 }}>
          👤 {playersLabel(game.id)}
        </span>
      </span>
    </button>
  );
}
